/**
 * VE-31538 — reading the lock on a request's merged document.
 *
 * ONE READER, two callers with different needs.
 * `serviceRefreshProjectSetLock` (documents) reads so it can decide whether
 * to take the lock and what verdict to publish to the open editor. The
 * set-wide write gates (rebuild, membership, delete, rename, Bates and
 * watermark) read so they can refuse. Both have to agree about what counts
 * as held, so the freshness rule and the ownership rule live here and
 * nowhere else.
 *
 * WHY THE GATES CANNOT JUST READ THE PUBLISHED VERDICT. `mergedSetBlock` is
 * published by the document editor's heartbeat, and two of these operations
 * — membership change and delete — are also reachable from the FOLDER VIEW,
 * where no editor session exists and that field is null. A gate reading it
 * there would wave every write through.
 */
import callGraphQLApi from '@helpers/callGraphQLApi';
import { lookupLatestTdoLockSchemaId } from '@cbsa-modules/universal/services';
import { LOCK_REFRESH } from '@helpers/constants';
import { modules } from '@veritone/glc-redux';
import type { Dispatch } from '@reduxjs/toolkit';

const {
  config: { getConfig },
} = modules;

/** The fields of a TDO-lock SDO that matter to the set lock. */
export interface TdoLockRecordData {
  readonly userId?: string;
  readonly name?: string;
  readonly lastAccessed?: string;
}

export interface SetLockVerdict {
  /**
   * This cluster has no TDO-lock registry configured, so locking is not set
   * up and there is nothing to hold. DISTINCT from a failed read: an org
   * that does not lock must not have its set-wide writes refused forever.
   *
   * Narrower than "the schema lookup came back empty", which is how this was
   * first decided — see `resolveLockSchema`.
   */
  readonly unavailable?: boolean;
  /**
   * The display name of ANOTHER user holding the set, when one does.
   * Undefined means the set is free to us — nobody holds it, or we do.
   */
  readonly heldBy?: string;
  /** Whether a foreign holder was found, since `heldBy` may be a blank name. */
  readonly isHeldByOther: boolean;
  /** Our own record's id, so a refresh updates it instead of minting one. */
  readonly ownRecordId?: string;
}

/**
 * Is this lock still being heartbeated?
 *
 * The same five-refresh-periods rule every other reader applies, against the
 * CLIENT's clock — a known weakness of the scheme, not something introduced
 * here (`plans/document-editor-lock-assessment.md` §2 weakness 4). A record
 * older than that belongs to a session that closed or crashed.
 */
export const isTdoLockFresh = (
  data: TdoLockRecordData | undefined
): boolean => {
  const lastAccessed = data?.lastAccessed;
  if (!lastAccessed) {
    return false;
  }
  const age = Date.now() - new Date(lastAccessed).getTime();
  return Number.isFinite(age) && age <= LOCK_REFRESH * 5;
};

/**
 * NEWEST FIRST, AND SAY HOW MANY.
 *
 * `asc` with the schema's default `limit: 30` asked for the thirty OLDEST
 * records — which, on a TDO where locks accumulate, is thirty dead ones.
 * Records leak on every unclean exit (release runs only on CLOSE_DOCUMENT
 * and LOGOUT), so a busy set would eventually return a page containing no
 * live lock at all: every gate would fail open and every banner vanish
 * while somebody was genuinely editing.
 *
 * A heartbeat is an update, so a live lock is by definition among the most
 * recently modified. Descending puts it on the first page whatever has piled
 * up behind it, and the explicit limit stops the page size being a default
 * we do not control.
 */
const LOCK_PAGE_SIZE = 50;

const findProjectTdoLocksQuery = `
  query findProjectTdoLocks($schemaId: ID!, $tdoId: ID!, $limit: Int!) {
    structuredDataObjects(
      schemaId: $schemaId
      filter: { tdoId: $tdoId }
      orderBy: { field: modifiedDateTime, direction: desc }
      limit: $limit
    ) {
      records { id data }
    }
  }`;

/**
 * The lock schema, and WHICH KIND OF NOTHING when there isn't one.
 *
 * `getLatestSchemaId` returns `undefined` for a 502, an expired token, an
 * authorization denial and a genuinely unprovisioned registry alike — the
 * same collapse that once diverted a whole session onto browser-local
 * storage and announced an unconfigured cluster on a healthy one (VE-28974,
 * see `watermarks/services`). Reading it as "this org does not lock" let one
 * bad response answer `unavailable` — which every set-wide write gate treats
 * as free — so a single unanswered request opened all seven of them, org
 * wide, silently and with nothing to retry.
 *
 * The registry ID is the honest divider. It comes from the cluster's config
 * and is the FIRST thing `getLatestSchemaId` checks: without one it never
 * queries at all, which is precisely the "not set up here" case. With one
 * configured, an empty answer is a request that did not work, and the
 * callers' own rules apply — the write gates refuse, the heartbeat waits for
 * the next tick.
 *
 * A configured registry whose schema was never published reads as
 * unreachable and keeps refusing. That is the right way round: nothing else
 * in the lock works there either — the per-file lock silently never takes —
 * so a refusal that says so beats set-wide writes running unguarded.
 */
const resolveLockSchema = async (
  dispatch: Dispatch,
  getState: () => unknown
): Promise<{ schemaId?: string; unprovisioned: boolean }> => {
  const schemaId = await lookupLatestTdoLockSchemaId(dispatch, getState);
  if (schemaId) {
    return { schemaId, unprovisioned: false };
  }
  const { tdoLockRegistryId } =
    getConfig<Window['config']>(getState() as never) ?? {};
  return { unprovisioned: !tdoLockRegistryId };
};

/**
 * Who holds the lock on `projectTdoId`, or `undefined` when the read failed.
 *
 * `undefined` means the QUERY failed, and is a third answer callers must
 * treat as such — `unavailable` on the verdict is the separate, benign case
 * of a cluster with no lock registry configured at all. Reporting
 * a failed read as "free" hands out a set-wide write on a set we know
 * nothing about; reporting it as "held" blocks every rebuild in the org over
 * one bad response. Each caller decides which way to lean, and they lean
 * differently — the heartbeat waits for the next tick, the write gates
 * refuse.
 */
export const serviceReadProjectSetLock =
  ({ projectTdoId, userId }: { projectTdoId: string; userId: string }) =>
  async (
    dispatch: Dispatch,
    getState: () => unknown
  ): Promise<SetLockVerdict | undefined> => {
    const schema = await resolveLockSchema(dispatch, getState);
    if (!schema.schemaId) {
      return schema.unprovisioned
        ? { isHeldByOther: false, unavailable: true }
        : undefined;
    }
    const { schemaId } = schema;

    const response = await callGraphQLApi<{
      structuredDataObjects: {
        records: { id: string; data: TdoLockRecordData }[];
      };
    }>({
      actionTypes: ['', '', ''],
      query: findProjectTdoLocksQuery,
      variables: { schemaId, tdoId: projectTdoId, limit: LOCK_PAGE_SIZE },
      dispatch: dispatch as never,
      getState: getState as never,
    });

    const records = response?.structuredDataObjects?.records;
    if (!records) {
      return undefined;
    }

    const fresh = records.filter(({ data }) => isTdoLockFresh(data));
    const foreign = fresh.find(({ data }) => data?.userId !== userId);
    return {
      heldBy: foreign?.data?.name,
      isHeldByOther: !!foreign,
      ownRecordId: fresh.find(({ data }) => data?.userId === userId)?.id,
    };
  };
